import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotifications, useMarkAllAsRead, NotificationWithActor } from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import { Loader2, BellOff } from 'lucide-react';

const TAB_FILTERS: Record<string, string[]> = {
  'for-you': ['post_like', 'post_comment', 'comment_reply', 'comment_like'],
  mentions: ['mention'],
  activity: ['connection_request', 'new_message', 'matchup', 'cohort_post'],
};

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <BellOff className="h-8 w-8 mb-2 opacity-40" />
      <p className="text-sm">No notifications yet</p>
    </div>
  );
}

function FilteredList({ notifications, types }: { notifications: NotificationWithActor[]; types: string[] }) {
  const filtered = notifications.filter(n => types.includes(n.type));
  if (filtered.length === 0) return <EmptyState />;
  return (
    <div className="divide-y divide-border">
      {filtered.map(n => (
        <NotificationItem key={n.id} notification={n} />
      ))}
    </div>
  );
}

export function NotificationList() {
  const { data: notifications, isLoading } = useNotifications();
  const markAllAsRead = useMarkAllAsRead();
  const [tab, setTab] = useState('for-you');

  const hasUnread = notifications?.some(n => !n.is_read);

  return (
    <div className="w-[360px] max-h-[480px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-foreground">Notifications</h3>
        {hasUnread && (
          <button
            onClick={() => markAllAsRead.mutate()}
            className="text-xs text-primary hover:underline"
            disabled={markAllAsRead.isPending}
          >
            Mark all read
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
          <TabsList className="grid grid-cols-3 mx-4 mt-2">
            <TabsTrigger value="for-you" className="text-xs">For You</TabsTrigger>
            <TabsTrigger value="mentions" className="text-xs">Mentions</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="for-you" className="mt-0">
              <FilteredList notifications={notifications || []} types={TAB_FILTERS['for-you']} />
            </TabsContent>
            <TabsContent value="mentions" className="mt-0">
              <FilteredList notifications={notifications || []} types={TAB_FILTERS.mentions} />
            </TabsContent>
            <TabsContent value="activity" className="mt-0">
              <FilteredList notifications={notifications || []} types={TAB_FILTERS.activity} />
            </TabsContent>
          </div>
        </Tabs>
      )}
    </div>
  );
}
